/**
 * Admin → System Health (read-only stack + worker monitor).
 *
 * Backed by GET /v1/admin/health. Same gate as the rest of the /v1/admin/*
 * admin block: RequireReseller + RequireRoleMin("admin"), so the sidebar link
 * is hidden for non-admins and the route guard blocks direct URL access.
 *
 * 100% read-only — the endpoint derives every component's status from
 * operational-table signals (SELECT-only, no worker/schema changes) and infers
 * voice health from the DB (no cross-box probe). Poll on an interval for
 * auto-refresh.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type HealthStatus = 'ok' | 'warn' | 'crit' | 'unknown';

export interface HealthComponent {
  /** Stable identifier, e.g. "sms_dispatcher". Drives the localized label. */
  key: string;
  /** English label from the API (fallback when no i18n key matches). */
  label: string;
  status: HealthStatus;
  /**
   * Concise primary metric — the single most important short value for this
   * component (e.g. "9ms", "928", "34m", "idle"). Shown big and neutral on the
   * tile; the status color lives on the bar/dot/tag only.
   */
  metric: string;
  /** Longer headline phrase, e.g. "backlog 0 · sent 1.2k/5m". */
  headline: string;
  /** One-line explanation of the current status. */
  detail: string;
  /** Raw numbers behind the status (counts, ages in seconds, latency). */
  metrics: Record<string, number>;
}

export interface HealthReport {
  /** Worst component status; "unknown" never escalates it. */
  overall: Exclude<HealthStatus, 'unknown'>;
  generated_at: string;
  components: HealthComponent[];
}

/** Poll cadence for the live auto-refresh (ms). */
export const HEALTH_REFETCH_MS = 20_000;

const HEALTH_KEY = ['admin', 'health'] as const;

/**
 * Fetch for /admin/health. When `live` is true the query re-polls every
 * HEALTH_REFETCH_MS; pausing (live=false) stops the interval.
 */
export function useAdminHealth(opts?: { live?: boolean; enabled?: boolean }) {
  const live = opts?.live ?? true;
  return useQuery({
    queryKey: HEALTH_KEY,
    queryFn: () => api.get<HealthReport>('/v1/admin/health'),
    refetchInterval: live ? HEALTH_REFETCH_MS : false,
    refetchOnWindowFocus: live,
    staleTime: 5_000,
    enabled: opts?.enabled ?? true,
  });
}
